import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { products, productCategories, companyInfo } from '@/mock';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Lightbulb, Box, Gauge, Plug, Fan, Zap, 
  CheckCircle2, ArrowRight, Filter, Home, ArrowLeft
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const iconMap = {
  Lightbulb, Box, Gauge, Plug, Fan, Zap
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || 'all');

  const handleCategoryChange = (slug) => {
    setSelectedCategory(slug);
    if (slug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: slug });
    }
  };

  const getFilteredProducts = () => {
    if (selectedCategory === 'all') {
      return products;
    }
    const category = productCategories.find(cat => cat.slug === selectedCategory);
    return category ? products.filter(p => category.productIds.includes(p.id)) : products;
  };

  const filteredProducts = getFilteredProducts();
  const activeCategory = productCategories.find(cat => cat.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Products - SAI INDUSTRIES | BIS, CIMFR & PESO Certified Flameproof Equipment</title>
        <meta name="description" content="Browse our complete range of BIS, CIMFR & PESO certified flameproof electrical equipment including lighting, junction boxes, control stations, switches, and motor starters." />
      </Helmet>

      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-slate-200 py-4 px-4 sm:px-6 lg:px-8 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-bold transition-colors">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/8z85qdqz_LOGO.png" 
              alt="SAI-EX Logo"
              className="h-10"
            />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-slate-800 text-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center">
          <Badge className="bg-orange-600 text-white mb-4 inline-flex border-0 text-sm sm:text-base px-4 py-2">
            Complete Product Range
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Flameproof Electrical Products
          </h1>
          <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto">
            BIS Approved, CIMFR & PESO Certified equipment for hazardous environments.
            Trusted by India's leading industries.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white sticky top-[72px] z-30 shadow-md">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Filter className="text-orange-600" size={24} />
            <h2 className="text-xl font-bold text-slate-900">Filter by Category</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleCategoryChange('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'border-slate-300 hover:border-orange-600'}
            >
              All Products ({products.length})
            </Button>
            {productCategories.map(category => {
              const Icon = iconMap[category.icon];
              const count = products.filter(p => category.productIds.includes(p.id)).length;
              return (
                <Button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.slug)}
                  variant={selectedCategory === category.slug ? 'default' : 'outline'}
                  className={selectedCategory === category.slug 
                    ? 'bg-orange-600 hover:bg-orange-700' 
                    : 'border-slate-300 hover:border-orange-600'}
                >
                  <Icon className="mr-2" size={16} />
                  {category.name} ({count})
                </Button>
              );
            })}
          </div>
          {activeCategory && (
            <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">{activeCategory.description}</p>
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {selectedCategory === 'all' 
                ? `All Products (${filteredProducts.length})`
                : `${activeCategory?.name} (${filteredProducts.length})`
              }
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredProducts.map((product, index) => (
              <Card 
                key={product.id}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-fade-in-up border-0 overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden bg-white h-64">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* SAI-Ex Brand Watermark */}
                  <img
                    src="https://customer-assets.emergentagent.com/job_sai-electrical/artifacts/51rvn1xg_FAVCON1.png"
                    alt="SAI-Ex Brand"
                    aria-hidden="true"
                    className="absolute bottom-2 right-2 w-14 sm:w-16 lg:w-20 h-auto opacity-60 mix-blend-multiply pointer-events-none select-none drop-shadow-sm"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-600 text-white border-0">
                      {product.catNo}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="mb-2">
                    <span className="sai-ex-branded-badge">SAI-Ex Branded</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 mb-4 text-sm line-clamp-2">
                    {product.description}
                  </p>
                  <div className="space-y-2 mb-4">
                    {product.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-xs text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white"
                    onClick={() => {/* Add contact or details action */}}
                  >
                    Get Quote <ArrowRight className="ml-2" size={16} />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-slate-600">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
